import {
  useId,
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  type ReactNode,
  type MouseEvent,
  type CSSProperties,
} from "react";
import { createPortal } from "react-dom";

const HOVER_OPEN_MS = 100;
const HOVER_CLOSE_MS = 280;
const GAP_PX = 6;

type HelpHintProps = {
  /** Uitleg in de popover; mag <strong> en <Link> bevatten. */
  children: ReactNode;
  /** Omschrijving van de knop voor schermlezers, bijv. "Uitleg: Leden" */
  label: string;
};

/**
 * Klein vraagteken naast een titel: toont uitleg bij hover (korte vertraging),
 * of bij klik (blijft open totdat je weer klikt, klikt buiten het gebied, of Esc drukt).
 * Popover wordt in document.body getoond (fixed + hoog z-index) zodat de balk
 * in het hoofdveld of scroll-container de tekst niet wegsnijdt.
 */
export default function HelpHint({ children, label }: HelpHintProps) {
  const id = useId();
  const [visible, setVisible] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState<CSSProperties>({});
  const sticky = useRef(false);
  const rootRef = useRef<HTMLSpanElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearOpen = useCallback(() => {
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
  }, []);

  const clearClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const scheduleOpen = useCallback(() => {
    if (sticky.current) return;
    clearClose();
    clearOpen();
    openTimer.current = setTimeout(() => {
      setVisible(true);
      openTimer.current = null;
    }, HOVER_OPEN_MS);
  }, [clearOpen, clearClose]);

  const scheduleClose = useCallback(() => {
    if (sticky.current) return;
    clearOpen();
    clearClose();
    closeTimer.current = setTimeout(() => {
      setVisible(false);
      closeTimer.current = null;
    }, HOVER_CLOSE_MS);
  }, [clearOpen, clearClose]);

  const handlePopoverEnter = useCallback(() => {
    clearClose();
  }, [clearClose]);

  const handlePopoverLeave = useCallback(() => {
    if (sticky.current) return;
    scheduleClose();
  }, [scheduleClose]);

  const updatePopoverPosition = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Rechts gelijk aan het vraagteken, breedte beperkt tot bestaande ruimte links van rechterkant
    const maxW = Math.min(384, Math.max(200, r.right - 8));
    setPopoverStyle({
      top: r.bottom + GAP_PX,
      right: Math.max(8, window.innerWidth - r.right),
      maxWidth: maxW,
    });
  }, []);

  useLayoutEffect(() => {
    if (!visible) {
      setPopoverStyle({});
      return;
    }
    updatePopoverPosition();
    window.addEventListener("scroll", updatePopoverPosition, true);
    window.addEventListener("resize", updatePopoverPosition);
    return () => {
      window.removeEventListener("scroll", updatePopoverPosition, true);
      window.removeEventListener("resize", updatePopoverPosition);
    };
  }, [visible, updatePopoverPosition]);

  const handleButtonClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      clearOpen();
      clearClose();
      sticky.current = !sticky.current;
      setVisible(sticky.current);
    },
    [clearOpen, clearClose],
  );

  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        sticky.current = false;
        setVisible(false);
      }
    };
    const onDocDown = (e: globalThis.MouseEvent) => {
      if (!(e.target instanceof Node)) return;
      const t = e.target;
      if (rootRef.current?.contains(t) || popoverRef.current?.contains(t)) return;
      sticky.current = false;
      setVisible(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDocDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDocDown);
    };
  }, [visible]);

  const popover =
    visible && typeof document !== "undefined"
      ? createPortal(
          <div
            id={id}
            ref={popoverRef}
            className="help-hint__popover help-hint__popover--portal"
            style={popoverStyle}
            role="tooltip"
            onPointerEnter={handlePopoverEnter}
            onPointerLeave={handlePopoverLeave}
          >
            <div className="help-hint__text">{children}</div>
          </div>,
          document.body,
        )
      : null;

  return (
    <span
      ref={rootRef}
      className="help-hint"
      onPointerEnter={scheduleOpen}
      onPointerLeave={scheduleClose}
    >
      <button
        type="button"
        className="help-hint__btn"
        aria-expanded={visible}
        aria-controls={visible ? id : undefined}
        aria-label={label}
        onClick={handleButtonClick}
      >
        ?
      </button>
      {popover}
    </span>
  );
}
